import { Paper, SimpleGrid, Text, Stack, Progress, Group } from '@mantine/core';
import type { ModalityProgressCard } from '../../../../types';
import { ModalityConfigPills, type ModalityConfigPillsModality } from '../ModalityConfigPills';

interface ModalityProgressSectionProps {
  cards: ModalityProgressCard[];
  // MED-1038: the cohort's modalities carry the facilitation config the cards do not;
  // we correlate by modality_type to render the per-modality config pills.
  modalities?: ModalityConfigPillsModality[];
}

export function ModalityProgressSection({ cards, modalities }: ModalityProgressSectionProps) {
  if (cards.length === 0) return null;

  return (
    <Stack gap="sm">
      <Text
        size="xs"
        fw={600}
        tt="uppercase"
        c="dimmed"
        style={{ letterSpacing: '0.05em' }}
      >
        Modality Progress
      </Text>

      <SimpleGrid cols={{ base: 1, sm: Math.min(cards.length, 3) }}>
        {cards.map(card => (
          <ModalityCard
            key={card.modality_type}
            card={card}
            modality={modalities?.find(m => m.modality_type === card.modality_type)}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
}

function ModalityCard({
  card,
  modality,
}: {
  card: ModalityProgressCard;
  modality?: ModalityConfigPillsModality;
}) {
  const sessionLabel = card.modality_type === 'workshop' ? 'completed' : 'sessions';

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{ borderColor: 'var(--mantine-color-gray-2)' }}
    >
      <Stack gap="xs">
        {/* MED-1038: config pills sit with the label on the left of the header row */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} c="dark">
              {card.label}
            </Text>
            <ModalityConfigPills modality={modality} />
          </Stack>
          <Text size="sm" fw={700} c="medleyGreen">
            {card.percentage}%
          </Text>
        </Group>

        <Progress
          value={card.percentage}
          color="medleyGreen"
          size="sm"
          radius="xl"
        />

        <Text size="xs" c="dimmed">
          {card.completed}/{card.total} {sessionLabel}
        </Text>
      </Stack>
    </Paper>
  );
}

export default ModalityProgressSection;
